// Voice Mode 2.0 — P1: $0 speech-to-text via the browser.
//
// The cascade is free because transcription happens *in the browser*
// (Web Speech API) — no STT provider, no per-minute cost. Chrome ends a
// `continuous` recognition session on its own after a while, so the wrapper
// transparently restarts while the caller still wants to listen.

export interface SpeechRecognitionLike {
  continuous: boolean;
  interimResults: boolean;
  lang: string;
  maxAlternatives?: number;
  start(): void;
  stop(): void;
  abort?(): void;
  onresult: ((event: unknown) => void) | null;
  onerror: ((event: unknown) => void) | null;
  onend: ((event?: unknown) => void) | null;
  onstart?: ((event?: unknown) => void) | null;
}

export type RecognitionFactory = () => SpeechRecognitionLike | null;

export interface SpeechTranscriptorOptions {
  factory?: RecognitionFactory;
  lang?: string;
  onFinal?: (text: string) => void;
  onInterim?: (text: string) => void;
  onError?: (error: string) => void;
}

/** Default factory: the vendor-prefixed Web Speech API, or null when absent. */
export function defaultRecognitionFactory(): SpeechRecognitionLike | null {
  const w = window as unknown as {
    SpeechRecognition?: new () => SpeechRecognitionLike;
    webkitSpeechRecognition?: new () => SpeechRecognitionLike;
  };
  const Ctor = w.SpeechRecognition ?? w.webkitSpeechRecognition;
  if (!Ctor) return null;
  return new Ctor();
}

export class SpeechTranscriptor {
  private readonly factory: RecognitionFactory;
  private recognition: SpeechRecognitionLike | null = null;
  private wantListening = false;
  private restarting = false;
  // How many finalised results have already been handed to the caller for the
  // current recognition session.  Web Speech re-sends the WHOLE result list on
  // every event, so without this the first utterance is re-delivered forever
  // and the agent keeps re-answering the first thing it heard
  // (owner report, 2026-09-20).
  private handledCount = 0;
  // The last final text we delivered, so a corrected/re-finalised version of
  // the previous result can still be passed on instead of being swallowed.
  private lastFinalText = '';
  onFinal?: (text: string) => void;
  onInterim?: (text: string) => void;
  onError?: (error: string) => void;

  constructor(options: SpeechTranscriptorOptions = {}) {
    this.factory = options.factory ?? defaultRecognitionFactory;
    this.lang = options.lang ?? (typeof navigator !== 'undefined' ? navigator.language || 'en-US' : 'en-US');
    this.onFinal = options.onFinal;
    this.onInterim = options.onInterim;
    this.onError = options.onError;
  }

  readonly lang: string;

  get supported(): boolean {
    if (this.factory === defaultRecognitionFactory) {
      return defaultRecognitionFactory() !== null;
    }
    try {
      return this.factory() !== null;
    } catch {
      return false;
    }
  }

  get listening(): boolean {
    return this.wantListening;
  }

  start(): boolean {
    if (this.wantListening) return true;
    const recognition = this.factory();
    if (!recognition) {
      this.onError?.('unsupported');
      return false;
    }
    this.wantListening = true;
    this.attach(recognition);
    try {
      recognition.start();
    } catch {
      // Chrome throws if a session is already running: restart on `onend`.
    }
    return true;
  }

  /**
   * Drop whatever audio this recogniser has buffered and start a clean session.
   *
   * Used when the microphone was listening to the assistant's own voice or when
   * the user barges in: without it, the tail of the interrupted audio is
   * transcribed a moment later and looks like a brand-new question.
   */
  flush(): void {
    const recognition = this.recognition;
    if (!recognition || !this.wantListening) return;
    try {
      recognition.abort?.();
    } catch {
      /* session already gone */
    }
  }

  stop(): void {
    this.wantListening = false;
    const recognition = this.recognition;
    this.recognition = null;
    if (!recognition) return;
    recognition.onresult = null;
    recognition.onerror = null;
    recognition.onend = null;
    try {
      recognition.stop();
    } catch {
      try {
        recognition.abort?.();
      } catch {
        /* already stopped */
      }
    }
  }

  private attach(recognition: SpeechRecognitionLike): void {
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.lang = this.lang;
    this.recognition = recognition;
    // A brand-new session starts a fresh result list.
    this.handledCount = 0;
    this.lastFinalText = '';

    recognition.onresult = (event: unknown) => {
      const results = (event as { results?: ArrayLike<ArrayLike<{ transcript?: string }> & { isFinal?: boolean }> })
        .results;
      if (!results) return;
      for (let i = 0; i < results.length; i += 1) {
        const result = results[i] as ArrayLike<{ transcript?: string }> & { isFinal?: boolean };
        const alternative = result?.[0];
        const transcript = (alternative?.transcript ?? '').trim();
        if (!transcript) continue;
        if (result.isFinal) {
          // Already handled this index in this session — skip it.  Exception:
          // the most recent final was CORRECTED by the engine (same index,
          // different text), which must reach the caller or the agent stays
          // stuck on the mis-transcribed version.
          if (i < this.handledCount) {
            if (i === this.handledCount - 1 && transcript !== this.lastFinalText) {
              this.lastFinalText = transcript;
              this.onFinal?.(transcript);
            }
            continue;
          }
          this.handledCount = i + 1;
          this.lastFinalText = transcript;
          this.onFinal?.(transcript);
        } else {
          if (i < this.handledCount) continue;
          this.onInterim?.(transcript);
        }
      }
    };

    recognition.onerror = (event: unknown) => {
      const code = String((event as { error?: string })?.error ?? 'error');
      // `no-speech` is normal in a quiet room; keep the session alive.
      if (code !== 'no-speech' && code !== 'aborted') this.onError?.(code);
    };

    recognition.onend = () => {
      if (!this.wantListening || this.restarting) return;
      this.restarting = true;
      // Restart on the next tick so `start()` is never called re-entrantly.
      window.setTimeout(() => {
        this.restarting = false;
        if (!this.wantListening) return;
        if (this.recognition !== recognition) return;
        // A restarted recognition session begins a fresh result list, so the
        // handled-index counter must begin again too (otherwise genuinely new
        // speech would be swallowed).
        this.handledCount = 0;
        try {
          recognition.start();
        } catch {
          /* ignore: a later `onend` will retry */
        }
      }, 250);
    };
  }
}
